import type * as bytescale from "@bytescale/sdk";
import { safeParse } from "valibot";
import { onUploadStatusChange } from "./helpers/events";
import { type UploadFilesArgs, UploadFilesArgsSchema } from "./schema";
import { getCanUpload, uploadAuthentication } from "./upload-authentication";
import { cancelledUploads, uploadFile } from "./upload-file";

const WAIT_INTERVAL = import.meta.env.VITEST ? 100 : 1000;

interface IUploadFilesArgs extends UploadFilesArgs {
  onFileSuccess?: (response: bytescale.FileDetails) => void;
  onFileError?: (file: File, error: Error) => void;
}

export async function uploadFiles({
  onFileError,
  onFileSuccess,
  ...args
}: IUploadFilesArgs) {
  const result = safeParse(UploadFilesArgsSchema, args);

  if (!result.success) {
    throw new Error(JSON.stringify(result.issues));
  }

  const { parallelUploads, files } = result.output;
  const maxConcurrentUploads = parallelUploads ?? files.length;

  files.forEach((file) => {
    onUploadStatusChange({
      uploadId: file.name,
      file,
      status: "initialised",
      key: args.key,
    });
  });

  let canUpload = getCanUpload();

  if (!canUpload) {
    await uploadAuthentication({
      accountId: args.accountId,
      authUrl: "/auth/bytescale",
      canUpload: args.canUpload,
    });

    canUpload = getCanUpload();
  }

  if (!canUpload) {
    files.forEach((file) => {
      onUploadStatusChange({
        status: "error",
        key: args.key,
        reason: "Upload not allowed",
        uploadId: file.name,
        file,
      });
    });
    throw new Error("User is not allowed to upload files");
  }

  let activeUploads: Array<File> = [];
  const hasFinished = false;

  while (!hasFinished) {
    if (!files.length && !activeUploads.length) break;

    if (activeUploads.length >= maxConcurrentUploads || !files.length) {
      await new Promise((resolve) => setTimeout(resolve, WAIT_INTERVAL));
      continue;
    }

    const file = files.shift() as File;
    if (cancelledUploads.includes(file.name)) {
      cancelledUploads.splice(cancelledUploads.indexOf(file.name), 1);
      continue;
    }

    activeUploads.push(file);

    const removeActiveUpload = () => {
      activeUploads = activeUploads.filter((activeFile) => activeFile !== file);
    };

    uploadFile({ ...args, file: file, uploadId: file.name })
      .then((response) => {
        onFileSuccess?.(response);
        removeActiveUpload();
      })
      .catch((reason) => {
        onFileError?.(file, reason as Error);
        removeActiveUpload();
      });
  }
}
